import { useParams, useLocation } from "react-router";
import { useClientPortalConfigContext } from "../../context/ClientPortalConfig.ctx";
import Content from "../Layout/Content";
import { Box, Card, Link, SxProps, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Calendar, CreditCard, Receipt } from "lucide-react";
import { useInvoiceDetails } from "./useInvoiceDetails";
import LoadingOverlay from "../Shared/LoadingOverlay";
import { amountToCurrency } from "../../utils/localeHelper";
import InvoiceStripePayment from "./InvoiceStripePayment";
import InvoiceDetail from "./InvoicePayDetail";
import { useMemo, useState } from "react";
import LoginDialog from "../Account/LoginDialog";

interface IInvoicePayUrlParams {
    clientId?: string;
    invoiceId?: string;
}

const invoiceCardSx: SxProps = { padding: 0 };

export default function InvoicePay() {
    const location = useLocation();
    const [showLoginDialog, setShowLoginDialog] = useState(false);
    const { clientId, invoiceId } = useParams() as IInvoicePayUrlParams;
    const { invoice, invoiceLoading } = useInvoiceDetails(clientId, invoiceId);
    const { clientPortalConfig } = useClientPortalConfigContext();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const invoiceDetailsSx: SxProps = useMemo(
        () => ({
            display: "flex",
            flexWrap: "nowrap",
            gap: isMobile ? "16px" : "40px",
            flexDirection: isMobile ? "column" : "row",
            padding: isMobile ? "24px" : "40px",
        }),
        [isMobile]
    );

    return (
        <Content
            dataTestIdSuffix="invoice_pay"
            sx={{ padding: "32px 8px 0", display: "flex", flexDirection: "column", gap: "16px" }}
        >
            <Typography variant="heading3">Invoice Payment</Typography>
            {invoiceLoading ? (
                <LoadingOverlay />
            ) : (
                <Card variant="outlined" sx={invoiceCardSx}>
                    {invoice && (
                        <Box sx={invoiceDetailsSx}>
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap="16px"
                                sx={{
                                    flex: isMobile ? "none" : "1 1 0",
                                    minWidth: 0,
                                }}
                            >
                                <InvoiceDetail
                                    dataTestIdSuffix="invoice_number"
                                    icon={Receipt}
                                    title="Invoice Number"
                                    value={String(invoice.invoiceNumber)}
                                />
                                <InvoiceDetail
                                    dataTestIdSuffix="invoice_date"
                                    icon={Calendar}
                                    title="Invoice Date"
                                    value={invoice.invoiceDate}
                                />
                                <InvoiceDetail
                                    dataTestIdSuffix="invoice_amount"
                                    icon={CreditCard}
                                    title="Amount to Pay"
                                    value={amountToCurrency(invoice.totalAmount, clientPortalConfig.currencySymbol)}
                                />
                                {!clientPortalConfig.userLoggedIn && !clientPortalConfig.isClientPortalDisabled && (
                                    <Box sx={{ marginTop: "auto" }}>
                                        <Link variant="small" onClick={() => setShowLoginDialog(true)}>
                                            Log in
                                        </Link>{" "}
                                        <Typography variant="paragraphSmallSemiBold">
                                            to pay with a saved card and see more details about this invoice
                                        </Typography>
                                        {showLoginDialog && (
                                            <LoginDialog
                                                handleClose={() => setShowLoginDialog(false)}
                                                returnUrl={location.pathname}
                                            />
                                        )}
                                    </Box>
                                )}
                            </Box>
                            <Box
                                sx={{
                                    flex: isMobile ? "none" : "1 1 0",
                                    minWidth: 0,
                                }}
                            >
                                <InvoiceStripePayment invoice={invoice} />
                            </Box>
                        </Box>
                    )}
                </Card>
            )}
        </Content>
    );
}
